import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, message, Modal, Input, Select, Collapse, DatePicker, Space, Spin, Tabs, Form, InputNumber, Row, Col, Divider, Upload } from 'antd';
import { SearchOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import { Box, MenuItem, FormControl, } from '@mui/material';
import { jsPDF } from 'jspdf';
import companyLogo from '../../../assets/company-logo.png';
import api from '../../../authentication/axiosInstance';
import { ArchiveWithAllocationsButton, UnarchiveButton } from '../../../components/allocation/ArchiveWithAllocationsModal';
import { sortVehiclesForDisplay } from '../../../utils/vehicleSortUtils';
import debounce from 'lodash/debounce';
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import useResizableSortableTable from '../../../hooks/useResizableSortableTable';
import DocumentThumbnail from '../../../components/DocumentThumbnail';

const { Option } = Select;
const { Panel } = Collapse;

dayjs.extend(customParseFormat);

const NEW_VEHICLE_DEFAULTS = { provider: 'n/a', vehicle_capacity: 0, vehicle_cost: 0, surface_type: 'lots' };
const getVehicleFormValues = (vehicle) => ({
  vehicle_name: vehicle.vehicle_name,
  vehicle_model: vehicle.vehicle_model,
  vehicle_make: vehicle.vehicle_make,
  vehicle_year: vehicle.vehicle_year,
  vehicle_type: vehicle.vehicle_type,
  vehicle_subtype: vehicle.vehicle_subtype,
  vehicle_capacity: vehicle.vehicle_capacity,
  vehicle_serial_number: vehicle.vehicle_serial_number,
  vehicle_cost: vehicle.vehicle_cost,
  provider: vehicle.provider,
  vin: vehicle.vin,
  trim: vehicle.trim,
  color: vehicle.color,
  license_plate: vehicle.license_plate,
  registration_state: vehicle.registration_state,
  registration_expiration_month: vehicle.registration_expiration_month,
  ownership: vehicle.ownership,
  fuel_type_name: vehicle.fuel_type_name,
  primary_meter_unit: vehicle.primary_meter_unit,
  primary_meter_value: vehicle.primary_meter_value,
  secondary_meter_unit: vehicle.secondary_meter_unit,
  secondary_meter_value: vehicle.secondary_meter_value,
  estimated_resale_price_cents: vehicle.estimated_resale_price_cents,
  msrp_cents: vehicle.msrp_cents,
  driver_name: vehicle.driver_name,
  driver_email: vehicle.driver_email,
  transponder_407_etr: vehicle.transponder_407_etr,
  gps_unit: vehicle.gps_unit,
  dashcam_id: vehicle.dashcam_id,
});

const EquipmentListView = () => {
  const [vehicles, setVehicles] = useState([]);
  const [archiveTab, setArchiveTab] = useState('active');
  const [showModal, setShowModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedValue, setSelectedValue] = useState(null);
  const [isAddingGenericLoader, setIsAddingGenericLoader] = useState(false);
  const [activeViewTabKey, setActiveViewTabKey] = useState('details');

  const [searchName, setSearchName] = useState('');
  const [searchType, setSearchType] = useState(undefined);
  const [searchModel, setSearchModel] = useState('');
  const [searchMake, setSearchMake] = useState(undefined);
  const [searchYear, setSearchYear] = useState(undefined);
  const [searchSubtype, setSearchSubtype] = useState(undefined);
  const [filteredVehicles, setFilteredVehicles] = useState([]);

  const [startDate, setStartDate] = useState(dayjs().startOf("year"));
  const [endDate, setEndDate] = useState(dayjs().startOf("year"));
  const [generatingTimesheet, setGeneratingTimesheet] = useState(false);

  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [selectedVehicleForTimesheet, setSelectedVehicleForTimesheet] = useState(null);

  // States for editing provider on a vehicle
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isCreatingVehicle, setIsCreatingVehicle] = useState(false);
  const [isSavingVehicle, setIsSavingVehicle] = useState(false);
  const [vehicleSubtypes, setVehicleSubtypes] = useState([]);
  const [loadingSubtypes, setLoadingSubtypes] = useState(false);
  const [subtypeLocked, setSubtypeLocked] = useState(null);
  const [displayCapacity, setDisplayCapacity] = useState(0);
  const [displayType, setDisplayType] = useState('');
  const [displaySurfaceType, setDisplaySurfaceType] = useState('');
  const [vehicleForm] = Form.useForm();
  const [selectedVehicleForEdit, setSelectedVehicleForEdit] = useState(null);
  const [editedProvider, setEditedProvider] = useState(null);

  // States for Fleetio history and files
  const [fleetioFiles, setFleetioFiles] = useState({});
  const [fleetioHistory, setFleetioHistory] = useState({});
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState({});
  const [filesLoaded, setFilesLoaded] = useState(false);
  const filesSession = useRef(0);
  const [historyLoaded, setHistoryLoaded] = useState({});
  // Removed Fleetio data states for meters, expenses, acquisitions, service reminders, 
  // faults, assignments, and submitted inspections (data preserved in DB/API, hidden from UI)

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };
  
  const handleEndDateChange = (date) => {
    setEndDate(date);
  };


  const generateTimeSheet = async () => {
    if (!startDate || !endDate) {
      message.error("Please select both start and end dates");
      return;
    }
  
    if (endDate.isBefore(startDate)) {
      message.error("End date must be after start date");
      return;
    }
  
    const startTimestamp = startDate.unix();
    const endTimestamp = endDate.unix();
  
    try {
      setGeneratingTimesheet(true);
      const response = await api.post("/viaesys/fetch-vehicle-site-visits/", {
        startTimestamp,
        endTimestamp,
      }, { responseType: "blob" });
  
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "vehicle_site_visits.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      message.success("Vehicle site visits downloaded successfully.");
    } catch (error) {
      console.error("Failed to fetch vehicle site visits:", error);
      if (error.response?.data?.error) {
        message.error(`Error: ${error.response.data.error}`);
      } else {
        message.error("Failed to fetch and download vehicle site visits.");
      }
    } finally {
      setGeneratingTimesheet(false);
    }
  };
  
  const handleGenerateSingleTimesheet = async () => {
    if (!startDate || !endDate) {
      message.error("Please select both start and end dates");
      return;
    }
  
    if (endDate.isBefore(startDate)) {
      message.error("End date must be after start date");
      return;
    }
  
    const startTimestamp = startDate.unix();
    const endTimestamp = endDate.unix();
  
    try {
      message.loading({ content: "Generating timesheet...", key: "timesheet" });
  
      const response = await api.post(
        "/viaesys/fetch-vehicle-site-visits-single/",
        {
          vehicleName: selectedVehicleForTimesheet?.vehicle_name, // Pass vehicle name
          startTimestamp,
          endTimestamp,
        },
        { responseType: "blob" }
      );
  
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${selectedVehicleForTimesheet?.vehicle_name}_site_visits.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      message.success({ content: "Timesheet downloaded successfully!", key: "timesheet" });
    } catch (error) {
      console.error("Failed to generate timesheet:", error);
      message.error({ content: "Failed to generate timesheet!", key: "timesheet" });
    } finally {
      setGenerateModalVisible(false);
    }
  };

  const handleGenerateModalCancel = () => {
    setGenerateModalVisible(false);
    setSelectedVehicleForTimesheet(null);
  };
  
  const openGenerateTimesheetModal = (vehicle) => {
    setSelectedVehicleForTimesheet(vehicle);
    setGenerateModalVisible(true);
  };
  
  const fetchVehicles = async () => {
    try {
      const response = await api.get('/vehicles/', {
        params: { archived: archiveTab === 'archived' },
      });
      const sortedVehicles = sortVehiclesForDisplay(response.data);
      setVehicles(sortedVehicles);
      setFilteredVehicles(sortedVehicles);
    } catch (error) {
      console.error("Failed to fetch vehicles:", error);
      message.error("Failed to fetch vehicles");
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, [archiveTab]);

  const addGenericLoader = async () => {
    if (!selectedValue) {
      message.error("Please select a generic loader subtype before adding a generic loader.");
      return;
    }

    try {
      setIsAddingGenericLoader(true);
      const response = await api.post('/vehicles/add_generic_loader/', {
        selected_value: selectedValue, // Include the selected dropdown value
      });
      message.success(`${response.data.vehicle_name} added successfully`);
      setSelectedValue(null);
      await fetchVehicles(); // Refresh the list
    } catch (error) {
      console.error("Failed to add generic loader:", error);
      if (error.response) {
        console.error("Error data:", error.response.data);
        console.error("Error status:", error.response.status);
        const errorMessages = {
          generic_vehicle_name_conflict:
            'Another generic loader was created at the same time. Please try again.',
          invalid_generic_subtype: 'Please select a valid generic loader subtype.',
          invalid_generic_vehicle: 'The generic loader name could not be generated.',
          generic_vehicle_creation_failed:
            'The generic loader could not be created. Please try again.',
        };
        const stableMessage = errorMessages[error.response.data?.code];
        message.error(
          stableMessage
          || `Failed to add generic loader: ${error.response.data?.error || 'Unknown error'}`,
        );
      } else if (error.request) {
        console.error("Error request:", error.request);
        message.error('Failed to add generic loader: No response received from server');
      } else {
        console.error('Error message:', error.message);
        message.error(`Failed to add generic loader: ${error.message}`);
      }
    } finally {
      setIsAddingGenericLoader(false);
    }
  };

  const generatePDF = (vehicle) => {
    if (!vehicle) {
      throw new Error('Error generating vehicle details pdf, no vehicle data provided');
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const lineSpacing = 8;
    const indentSpacing = 10;
  
    doc.addImage(companyLogo, 'PNG', margin, margin, 66, 13);

    doc.setFontSize(16);
    const headerText = `${vehicle.vehicle_name} - Vehicle Details`;
    const headerTextWidth = doc.getTextWidth(headerText);
    const rightAlignedX = pageWidth - headerTextWidth - 10;
    doc.text(`${vehicle.vehicle_name} - Vehicle Details`, rightAlignedX, 17);

    doc.setFontSize(14);
    const xPos = 20;
    let yPos = 40;
    let columnSpacing = 70;

    const vehicleCost = vehicle.vehicle_cost
      ? Number(vehicle.vehicle_cost).toFixed(2)
      : '0.00';

    const details = [
      { label: 'Vehicle Name:', value: vehicle.vehicle_name },
      { label: 'Vehicle Type:', value: vehicle.vehicle_type },
      { label: 'Sub Type:', value: vehicle.vehicle_subtype },
      { label: 'Model:', value: vehicle.vehicle_model },
      { label: 'Make:', value: vehicle.vehicle_make },
      { label: 'Year:', value: vehicle.vehicle_year },
      { label: 'Serial Number:', value: vehicle.vehicle_serial_number },
      { label: 'Capacity:', value: vehicle.vehicle_capacity.toString() },
      { label: 'Remaining Capacity:', value: vehicle.remaining_capacity.toString() },
      { label: 'Vehicle Costs:', value: `$${vehicleCost}` },
      { label: 'Surface Type:', value: vehicle.surface_type },
      { label: 'Number of Staff Assigned:', value: vehicle.number_staff_assigned.toString() },
      { label: 'Status:', value: vehicle.status },
      { label: 'Plow Size:', value: vehicle.plow_size },
    ];

    details.forEach((detail) => {
      doc.text(detail.label, xPos, yPos);
      doc.text(detail.value || '', xPos + columnSpacing, yPos);
      yPos += lineSpacing;
    });

    if (vehicle.assigned_plow) {
      yPos += lineSpacing;
      doc.setFontSize(14);
      doc.text('Assigned Plow Details:', xPos, yPos);
      yPos += lineSpacing;
      doc.setFontSize(12);

      const plowDetails = [
        { label: 'Plow Name:', value: vehicle.assigned_plow.plow_name },
        { label: 'Plow Model:', value: vehicle.assigned_plow.plow_model }
      ];

      columnSpacing = 40;
      plowDetails.forEach((detail) => {
        doc.text(detail.label, xPos + indentSpacing, yPos);
        doc.text(detail.value || '', xPos + columnSpacing, yPos);
        yPos += lineSpacing;
      });
    }

    if (vehicle.allocated_sites && vehicle.allocated_sites.length > 0) {
      yPos += lineSpacing;
      doc.setFontSize(14);
      doc.text('Allocated Sites:', xPos, yPos);
      yPos += lineSpacing;
      doc.setFontSize(12);

      vehicle.allocated_sites.forEach((site) => {
        doc.text(`- ${site.site_name} (Client: ${site.client_name ? site.client_name : 'N/A'})`, xPos + indentSpacing, yPos);
        yPos += lineSpacing;
      });
    }

    doc.save(`${vehicle.vehicle_name}.pdf`);
  };
  
  // ----------------------------
  // Handlers for Editing the Vehicle
  // ----------------------------
  const [activeTabKey, setActiveTabKey] = useState('details');

  const openEditModal = (vehicle) => {
    filesSession.current += 1;
    setLoadingFiles(false);
    setIsCreatingVehicle(false);
    setLoadingSubtypes(true);
    setSubtypeLocked(null);
    setDisplayCapacity(vehicle.vehicle_capacity ?? 0);
    setDisplayType(vehicle.vehicle_type ?? '');
    setDisplaySurfaceType(vehicle.surface_type ?? '');
    setSelectedVehicleForEdit(vehicle);
    setEditedProvider(vehicle.provider); // preload with the current provider
    setActiveTabKey('details'); // Reset to first tab
    setFleetioFiles({});
    setFleetioHistory({});
    setFilesLoaded(false);
    setHistoryLoaded({});
    setIsEditModalVisible(true);
  };

  const handleEditModalCancel = () => {
    if (isSavingVehicle) return;
    filesSession.current += 1;
    setIsEditModalVisible(false);
    setSelectedVehicleForEdit(null);
    setEditedProvider(null);
    setActiveTabKey('details');
    setFleetioFiles({});
    setFleetioHistory({});
    setFilesLoaded(false);
    setHistoryLoaded({});
  };

  const openAddVehicleModal = () => {
    openEditModal({ ...NEW_VEHICLE_DEFAULTS });
    setIsCreatingVehicle(true);
  };

  const editVehicleId = selectedVehicleForEdit?.vehicle_id;
  useEffect(() => {
    if (!isEditModalVisible) return undefined;
    let cancelled = false;
    vehicleForm.resetFields();
    setLoadingSubtypes(true);
    setSubtypeLocked(null);
    setVehicleSubtypes([]);
    Promise.all([
      api.get('/vehicle-subtypes/'),
      editVehicleId
        ? api.get(`/vehicles/${editVehicleId}/edit_constraints/`)
        : Promise.resolve({ data: { subtype_locked: false } }),
      editVehicleId ? api.get(`/vehicles/${editVehicleId}/`) : Promise.resolve({ data: { ...NEW_VEHICLE_DEFAULTS } }),
    ]).then(([{ data }, { data: constraints }, { data: vehicle }]) => {
      if (cancelled) return;
      if (editVehicleId && vehicle?.vehicle_id !== editVehicleId) throw new Error('Invalid vehicle details');
      setSelectedVehicleForEdit(vehicle);
      setEditedProvider(vehicle.provider);
      setDisplayCapacity(vehicle.vehicle_capacity ?? 0);
      setDisplayType(vehicle.vehicle_type ?? '');
      setDisplaySurfaceType(vehicle.surface_type ?? '');
      vehicleForm.resetFields();
      vehicleForm.setFieldsValue(getVehicleFormValues(vehicle));
      const subtypes = Array.isArray(data) ? data.map(subtype => subtype.vehicle_subtype === 'Other'
        ? { ...subtype, vehicle_type: 'Other', vehicle_capacity: 0 } : subtype) : [];
      setVehicleSubtypes(subtypes);
      setSubtypeLocked(Boolean(constraints?.subtype_locked));
      const subtype = subtypes.find(item => item.vehicle_subtype === vehicle.vehicle_subtype);
      if (constraints?.subtype_locked) {
        setDisplayCapacity(constraints.vehicle_capacity ?? vehicle.vehicle_capacity);
        setDisplayType(constraints.vehicle_type ?? vehicle.vehicle_type);
      } else if (subtype) {
        setDisplayCapacity(subtype.vehicle_capacity);
        setDisplayType(subtype.vehicle_type);
        setDisplaySurfaceType(subtype.surface_type ?? '');
      }
    }).catch(() => {
      if (!cancelled) message.error('Could not load vehicle subtypes and allocation checks. Please reopen the form to try again.');
    }).finally(() => {
      if (!cancelled) setLoadingSubtypes(false);
    });
    return () => { cancelled = true; };
  }, [isEditModalVisible, editVehicleId, vehicleForm]);

  const handleEditModalSubmit = async (values) => {
    if (loadingSubtypes || subtypeLocked === null) return;
    if (!values.provider) {
      message.error("Please select a provider.");
      return;
    }
    if (!values.vehicle_name || !values.vehicle_name.trim()) {
      message.error("Vehicle name cannot be empty.");
      return;
    }
    if (!values.vehicle_model || !values.vehicle_model.trim()) {
      message.error("Vehicle model cannot be empty.");
      return;
    }
    try {
      setIsSavingVehicle(true);
      const updateData = {
        ...values,
        vehicle_name: values.vehicle_name.trim(),
        vehicle_model: values.vehicle_model.trim(),
        vehicle_type: displayType,
      };
      delete updateData.vehicle_capacity;
      delete updateData.surface_type;
      
      if (isCreatingVehicle) {
        await api.post('/vehicles/', updateData);
      } else {
        await api.patch(`/vehicles/${selectedVehicleForEdit.vehicle_id}/patch_vehicle/`, updateData);
      }
      message.success(isCreatingVehicle ? "Vehicle added successfully." : "Vehicle updated successfully.");
      filesSession.current += 1;
      setIsEditModalVisible(false);
      if (isCreatingVehicle && archiveTab !== 'active') {
        setArchiveTab('active');
      } else {
        await fetchVehicles();
      }
    } catch (error) {
      console.error("Failed to update vehicle:", error);
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.vehicle_name) {
          message.error(`Vehicle name error: ${errorData.vehicle_name}`);
        } else if (errorData.vehicle_model) {
          message.error(`Error: ${errorData.vehicle_model}`);
        } else {
          message.error(Object.entries(errorData).map(([field, errors]) => `${field}: ${[].concat(errors).join(' ')}`).join('; '));
        }
      } else {
        message.error(isCreatingVehicle ? "Failed to add vehicle." : "Failed to update vehicle.");
      }
    } finally {
      setIsSavingVehicle(false);
    }
  };

  const handleEditFormFailed = ({ errorFields }) => {
    // Switch to the tab containing the first validation error
    if (errorFields && errorFields.length > 0) {
      const firstErrorField = errorFields[0].name[0];
      
      // Map fields to their respective tabs (updated for simplified UI)
      const fieldToTabMap = {
        vehicle_name: 'details',
        vehicle_model: 'details',
        color: 'details',
        driver_name: 'details',
        driver_email: 'details',
        provider: 'details',
        fuel_type_name: 'details',
        primary_meter_unit: 'details',
        primary_meter_value: 'details',
        ownership: 'registration',
        license_plate: 'registration',
        registration_state: 'registration',
        registration_expiration_month: 'registration',
        msrp_cents: 'registration',
        estimated_resale_price_cents: 'registration',
        transponder_407_etr: 'registration',
        gps_unit: 'registration',
        dashcam_id: 'registration',
      };

      
      const targetTab = fieldToTabMap[firstErrorField];
      if (targetTab) {
        setActiveTabKey(targetTab);
        message.error(`Please check the ${targetTab === 'registration' ? 'Registration & Ownership' : 'Details'} tab for errors`);
      }
    }
  };

  // Fetch Fleetio files for a vehicle
  const fetchVehicleFiles = async (vehicleId) => {
    if (filesLoaded) return; // Already loaded
    const session = filesSession.current;
    
    setLoadingFiles(true);
    try {
      const response = await api.get(`/vehicles/${vehicleId}/files/`);
      if (session !== filesSession.current) return;
      setFleetioFiles(response.data);
      setFilesLoaded(true);
    } catch (error) {
      if (session !== filesSession.current) return;
      console.error('Failed to fetch vehicle files:', error);
      setFleetioFiles({});
    } finally {
      if (session === filesSession.current) setLoadingFiles(false);
    }
  };

  // Fetch Fleetio history for a vehicle
  const fetchVehicleHistory = async (vehicleId, kind) => {
    if (historyLoaded[kind]) return; // Already loaded this kind
    
    setLoadingHistory(prev => ({ ...prev, [kind]: true }));
    try {
      const response = await api.get(`/vehicles/${vehicleId}/history/`, {
        params: { kind, page_size: 50 }
      });
      setFleetioHistory(prev => ({ ...prev, [kind]: response.data.results || [] }));
      setHistoryLoaded(prev => ({ ...prev, [kind]: true }));
    } catch (error) {
      console.error(`Failed to fetch vehicle ${kind} history:`, error);
      setFleetioHistory(prev => ({ ...prev, [kind]: [] }));
    } finally {
      setLoadingHistory(prev => ({ ...prev, [kind]: false }));
    }
  };

  // Removed Fleetio data fetching functions (data still accessible via API endpoints, 
  // not shown in UI per PM requirements)

  // Handle tab change - lazy load data (simplified for PM requirements)
  const handleTabChange = (key) => {
    setActiveTabKey(key);
    
    if (!selectedVehicleForEdit?.vehicle_id) return;
    
    const vehicleId = selectedVehicleForEdit.vehicle_id;
    
    // Lazy load files for the Documents & Photos tab only
    if (key === 'files' && !filesLoaded) {
      fetchVehicleFiles(vehicleId);
    }
  };


  const columns = [
    {
      title: 'Vehicle Name',
      dataIndex: 'vehicle_name',
      key: 'vehicle_name',
      sortType: 'text',
    },
    {
      title: 'Type',
      dataIndex: 'vehicle_type',
      key: 'vehicle_type',
      sortType: 'text',
    },
    {
      title: 'Subtype',
      dataIndex: 'vehicle_subtype',
      key: 'vehicle_subtype',
      sortType: 'text',
    },
    {
      title: 'Plow Size',
      dataIndex: 'plow_size',
      key: 'plow_size',
      sortType: 'text',
    },
    {
      title: 'Model',
      dataIndex: 'vehicle_model',
      key: 'vehicle_model',
      sortType: 'text',
    },
    {
      title: 'Make',
      dataIndex: 'vehicle_make',
      key: 'vehicle_make',
      sortType: 'text',
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      sortType: 'text',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 560,
      sortable: false,
      render: (record) => (
        <div className="action-buttons" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
          <Button
            onClick={() => openGenerateTimesheetModal(record)}
            type="primary"
            style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
          >
            Generate Timesheet
          </Button>
          <Button
            onClick={() => showVehicleDetails(record)}
            type="primary"
            style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
          >
            View Details
          </Button>
          {archiveTab === 'active' ? (
            <>
              <Button
                onClick={() => openEditModal(record)}
                type="primary"
                icon={<EditOutlined />}
                style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
              >
                Edit
              </Button>
              <ArchiveWithAllocationsButton
                itemType="vehicle"
                itemId={record.vehicle_id}
                itemLabel={record.vehicle_name}
                onArchived={fetchVehicles}
                buttonProps={{ className: 'delete-button' }}
              />
            </>
          ) : (
            <UnarchiveButton
              itemType="vehicle"
              itemId={record.vehicle_id}
              itemLabel={record.vehicle_name}
              onUnarchived={fetchVehicles}
            />
          )}
        </div>
      ),
    },
    ...(archiveTab === 'archived' ? [
      {
        title: 'Archived At',
        dataIndex: 'archived_at',
        key: 'archived_at',
        sortType: 'date',
        render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—',
      },
    ] : []),
  ];

  // Use the resizable and sortable table hook
  const { sortedData, enhancedColumns, ResizableTitle } = useResizableSortableTable(
    'vehicles-table',
    columns,
    filteredVehicles
  );

  // Small display helper for read-only detail fields
  const renderField = (label, value) => (
    <div style={{ marginBottom: '16px' }}>
      <strong>{label}</strong>
      <div>{value}</div>
    </div>
  );

  // Documents + registration/identification photos, shared by view and edit modals
  const handleVehicleFileUpload = async (file, category) => {
    if (!selectedVehicle) return false;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    
    try {
      await api.post(`/vehicles/${selectedVehicle.vehicle_id}/files/upload/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      message.success(`${category === 'Document' ? 'Document' : 'Photo'} uploaded successfully`);
      fetchVehicleFiles(selectedVehicle.vehicle_id);
      return false;
    } catch (error) {
      console.error('Failed to upload file:', error);
      message.error(error.response?.data?.error || `Failed to upload ${category.toLowerCase()}`);
      return false;
    }
  };

  const renderVehicleFiles = () => (
    <>
      <Divider orientation="left">Documents</Divider>
      <div style={{ marginBottom: '16px' }} data-testid="vehicle-document-upload">
        <Upload
          beforeUpload={(file) => handleVehicleFileUpload(file, 'Document')}
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />}>Upload Document</Button>
        </Upload>
      </div>
      {loadingFiles ? (
                  <div style={{ textAlign: 'center', padding: '48px' }}><Spin /></div>
                ) : (fleetioFiles['Document'] && fleetioFiles['Document'].length > 0) ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '16px' }}>
                    {fleetioFiles['Document'].map((file, idx) => (
                      <DocumentThumbnail key={idx} file={file} />
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '48px 24px', color: '#999' }}>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>📄 Documents</p>
                    <p>No documents found</p>
                  </div>
                )}
      <Divider orientation="left">Registration/Identification Photos</Divider>
      <div style={{ marginBottom: '16px' }} data-testid="vehicle-photo-upload">
        <Upload
          beforeUpload={(file) => handleVehicleFileUpload(file, 'Photo')}
          accept="image/*"
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />}>Upload Photo</Button>
        </Upload>
      </div>
      {loadingFiles ? (
                  <div style={{ textAlign: 'center', padding: '48px' }}><Spin /></div>
                ) : (fleetioFiles['Photo'] && fleetioFiles['Photo'].length > 0) ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                    {fleetioFiles['Photo'].map((file, idx) => (
                      <a key={idx} href={file.presigned_url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={file.presigned_url}
                          alt={file.filename}
                          style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                      </a>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '48px 24px', color: '#999' }}>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>📷 Registration/Identification Photos</p>
                    <p>No photos found</p>
                  </div>
                )}
    </>
  );

  const handleModalCancel = () => {
    filesSession.current += 1;
    setShowModal(false);
    setSelectedVehicle(null);
    setActiveViewTabKey('details');
    // Reset lazy-loaded data
    setFleetioFiles({});
    setFleetioHistory({});
    setFilesLoaded(false);
    setHistoryLoaded({});
  };

  const showVehicleDetails = (vehicle) => {
    filesSession.current += 1;
    setFleetioFiles({});
    setFilesLoaded(false);
    setLoadingFiles(false);
    setSelectedVehicle(vehicle);
    setShowModal(true);
    setActiveViewTabKey('details');
  };

  // Handle view modal tab change - lazy load data (simplified for PM requirements)
  const handleViewTabChange = (key) => {
    setActiveViewTabKey(key);
    
    if (!selectedVehicle) return;
    
    const vehicleId = selectedVehicle.vehicle_id;
    
    // Lazy load files for the Documents & Photos tab only
    if (key === 'files' && !filesLoaded) {
      fetchVehicleFiles(vehicleId);
    }
  };

  //Filtering and searching
  const debouncedSearchName = debounce((value) => {
    handleSearchName(value);
  }, 300);

  const handleSearchName = (value) => {
    setSearchName(value);
  };


  const debouncedSearchModel = debounce((value) => {
    handleSearchModel(value);
  }, 300);

  const handleSearchModel = (value) => {
    setSearchModel(value);
  };
  
  const applyFilters = () => {
    const filtered = vehicles.filter((vehicle) => {
      const filterName = vehicle.vehicle_name.toLowerCase().includes(searchName.toLowerCase());
      const filterType = searchType ? vehicle.vehicle_type.toLowerCase().includes(searchType.toLowerCase()) : true;
      const filterModel = vehicle.vehicle_model.toLowerCase().includes(searchModel.toLowerCase());
      const filterMake = searchMake ? vehicle.vehicle_make.toLowerCase().includes(searchMake.toLowerCase()) : true;
      const filterYear = searchYear ? vehicle.vehicle_year.toString() === searchYear.toString() : true;
      const filterSubtype = searchSubtype ? vehicle.vehicle_subtype.toString() === searchSubtype.toString() : true;
      return filterName && filterType && filterModel && filterMake && filterYear && filterSubtype;
    });
    setFilteredVehicles(filtered);
  };
  
  useEffect(() => {
    applyFilters();
  }, [searchName, searchType, searchModel, searchMake, searchYear, searchSubtype]);


  return (
    <Box style={{ padding: 30 }}>
      <Modal
        visible={generatingTimesheet}
        title={null}
        footer={null}
        centered
        closable={false}
        maskClosable={false}
        bodyStyle={{ textAlign: 'center', padding: 40 }}
      >
        <Spin size="large" style={{ marginBottom: 20 }} />
        <h2>Generating Vehicle Timesheet</h2>
        <p>Please wait while we create your csv file...</p>
      </Modal>
      {/* Modal for selecting date range and generating timesheet */}
      <Modal
        title={`Generate Timesheet for ${selectedVehicleForTimesheet?.vehicle_name}`}
        open={generateModalVisible}
        onCancel={handleGenerateModalCancel}
        footer={[
          <Button key="cancel" onClick={handleModalCancel}>
            Cancel
          </Button>,
          <Button
            key="generate"
            type="primary"
            onClick={handleGenerateSingleTimesheet}
          >
            Generate Timesheet CSV
          </Button>,
        ]}
      >
        <Space direction="vertical" size="large">
          <div>
            <label style={{ paddingRight: 10 }}>Start Date:</label>
            <DatePicker
              showTime={{ format: "HH:mm", defaultValue: dayjs("00:00", "HH:mm") }}
              format="YYYY-MM-DD HH:mm"
              value={startDate}
              onChange={(date) => setStartDate(date)}
              allowClear={false}
            />
          </div>
          <div>
            <label style={{ paddingRight: 10 }}>End Date:</label>
            <DatePicker
              showTime={{ format: "HH:mm", defaultValue: dayjs("00:00", "HH:mm") }}
              format="YYYY-MM-DD HH:mm"
              value={endDate}
              onChange={(date) => setEndDate(date)}
              allowClear={false}
            />
          </div>
        </Space>
      </Modal>
      <h3 style={{fontWeight:"bold",}}>
        Vehicles:
      </h3>
      <Box style={{ marginBottom: 16, display: 'flex' }}>
        <div style={{display:"flex", justifyContent:'space-between', width:'100%'}}>
          <div>
            <Button type="primary" onClick={openAddVehicleModal} style={{ marginRight: 10 }}>
              Add Vehicle
            </Button>
            <Button
              onClick={addGenericLoader}
              type="primary"
              loading={isAddingGenericLoader}
              disabled={isAddingGenericLoader}
              aria-label="Add generic loader"
            >
              Add Generic Loader
            </Button>
            <Select
              value={selectedValue}
              allowClear
              onChange={(value) => setSelectedValue(value)}
              placeholder="Generic Loader Subtype"
              aria-label="Generic loader subtype"
              style={{ width: 250 , marginLeft: 10}}
            >
              <Option value="321">321</Option>
              <Option value="344">344</Option>
              <Option value="444">444</Option>
              <Option value="621">621</Option>
              <Option value="926">926</Option>
              <Option value="930">930</Option>
              <Option value="938">938</Option>
              <Option value="skidsteer">skidsteer</Option>
            </Select>
          </div>
          <div>
            <Input.Search
              placeholder="Search name"
              onSearch={setSearchName}
              onChange={(e) => debouncedSearchName(e.target.value)}
              style={{ width: 200, marginRight: 8 }}
              prefix={<SearchOutlined />}
            />
            <Select
              placeholder="Search by type"
              value={searchType}
              onChange={(value) => setSearchType(value)} // Updates searchType
              style={{ width: 200, marginRight: 8 }}
              allowClear
            >
              {Array.from(new Set(vehicles.map((vehicle) => vehicle.vehicle_type))) // Extract unique types
                .map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
              ))}
            </Select>
            <Select
              placeholder="Search by subtype"
              value={searchSubtype}
              onChange={(value) => setSearchSubtype(value)} // Updates searchType
              style={{ width: 200, marginRight: 8 }}
              allowClear
            >
              {Array.from(new Set(vehicles.map((vehicle) => vehicle.vehicle_subtype))) // Extract unique types
                .map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
              ))}
            </Select>
            <Input.Search
              placeholder="Search model"
              onSearch={setSearchModel}
              onChange={(e) => debouncedSearchModel(e.target.value)}
              style={{ width: 200, marginRight: 8 }}
              prefix={<SearchOutlined />}
            />
            <Select
              placeholder="Search by make"
              value={searchMake} // Dropdown value
              onChange={(value) => setSearchMake(value)} // Updates searchMake
              style={{ width: 200, marginRight: 8 }}
              allowClear
            >
              {Array.from(new Set(vehicles.map((vehicle) => vehicle.vehicle_make))) // Extract unique makes
                .map((make) => (
                  <Option key={make} value={make}>
                    {make}
                  </Option>
              ))}
            </Select>
          </div>
        </div>
      </Box>
      <Tabs
        activeKey={archiveTab}
        onChange={setArchiveTab}
        style={{ marginBottom: 12 }}
        items={[
          { key: 'active', label: 'Active' },
          { key: 'archived', label: 'Archived' },
        ]}
      />
      <Table 
        dataSource={sortedData} 
        columns={enhancedColumns} 
        rowKey="vehicle_id" 
        pagination={false} 
        scroll={{ y: 640 }}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
      />
      <Modal
        title="Vehicle Details"
        open={showModal}
        onCancel={handleModalCancel}
        footer={[
          <Button key="download" onClick={() => generatePDF(selectedVehicle)}>
              Download PDF
          </Button>,
        ]}
        width={900}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {selectedVehicle && (
          <Tabs
            activeKey={activeViewTabKey}
            onChange={handleViewTabChange}
            destroyInactiveTabPane={false}
            items={[
              {
                key: 'details',
                label: 'Details',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      {renderField('Name:', selectedVehicle.vehicle_name || 'N/A')}
                      {renderField('Type:', selectedVehicle.vehicle_type || 'N/A')}
                      {renderField('Sub Type:', selectedVehicle.vehicle_subtype || 'N/A')}
                      {renderField('Make:', selectedVehicle.vehicle_make || 'N/A')}
                      {renderField('Model:', selectedVehicle.vehicle_model || 'N/A')}
                      {renderField('Year:', selectedVehicle.vehicle_year || 'N/A')}
                      {renderField('Serial Number:', selectedVehicle.vehicle_serial_number || 'N/A')}
                      {renderField('Capacity:', selectedVehicle.vehicle_capacity || 0)}
                      {renderField('Vehicle Costs:', `$${selectedVehicle.vehicle_cost ? parseFloat(selectedVehicle.vehicle_cost).toFixed(2) : '0.00'}`)}
                    </Col>
                    <Col span={12}>
                      {renderField('Surface Type:', selectedVehicle.surface_type || 'N/A')}
                      {renderField('Status:', selectedVehicle.status || 'N/A')}
                      {renderField('Color:', selectedVehicle.color || 'N/A')}
                      {renderField('Provider:', selectedVehicle.provider || 'N/A')}
                      {renderField('Fuel Type:', selectedVehicle.fuel_type_name || 'N/A')}
                      {renderField('Current Meter:', selectedVehicle.primary_meter_value ? `${selectedVehicle.primary_meter_value} ${selectedVehicle.primary_meter_unit || ''}`.trim() : 'N/A')}
                      {renderField('Driver Name:', selectedVehicle.driver_name || 'N/A')}
                      {renderField('Driver Email:', selectedVehicle.driver_email || 'N/A')}
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'registration',
                label: 'Registration & Ownership',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      {renderField('License Plate:', selectedVehicle.license_plate || 'N/A')}
                      {renderField('State/Province:', selectedVehicle.registration_state || 'N/A')}
                      {renderField('Expiry Month:', selectedVehicle.registration_expiration_month || 'N/A')}
                      {renderField('Ownership:', selectedVehicle.ownership || 'N/A')}
                      {renderField('MSRP:', `$${((selectedVehicle.msrp_cents || 0) / 100).toFixed(2)}`)}
                    </Col>
                    <Col span={12}>
                      {renderField('Estimated Resale Price:', `$${((selectedVehicle.estimated_resale_price_cents || 0) / 100).toFixed(2)}`)}
                      {renderField('Transponder 407 ETR:', selectedVehicle.transponder_407_etr || 'N/A')}
                      {renderField('GPS Unit:', selectedVehicle.gps_unit || 'N/A')}
                      {renderField('Dashcam ID:', selectedVehicle.dashcam_id || 'N/A')}
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'files',
                label: 'Documents & Photos',
                children: renderVehicleFiles(),
              },
            ]}
          />
        )}
      </Modal>


            {/* Edit Vehicle Modal */}
            <Modal
        title={isCreatingVehicle ? 'Add Vehicle' : 'Edit Vehicle'}
        open={isEditModalVisible}
        destroyOnClose
        onCancel={handleEditModalCancel}
        footer={null}
        width={900}
        closable={!isSavingVehicle}
        maskClosable={!isSavingVehicle}
        keyboard={!isSavingVehicle}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {loadingSubtypes && <Spin aria-label="Loading vehicle form" />}
        {!loadingSubtypes && subtypeLocked === null && <p role="alert">Could not load the form. Close and reopen it to try again.</p>}
        {selectedVehicleForEdit && (
          <Form
            form={vehicleForm}
            key={isCreatingVehicle ? 'new-vehicle' : selectedVehicleForEdit.vehicle_id}
            preserve={false}
            initialValues={getVehicleFormValues(selectedVehicleForEdit)}
            disabled={loadingSubtypes || subtypeLocked === null}
            style={{ visibility: loadingSubtypes || subtypeLocked === null ? 'hidden' : 'visible' }}
            onFinish={handleEditModalSubmit}
            onFinishFailed={handleEditFormFailed}
            layout="vertical"
          >
            <Tabs
              activeKey={activeTabKey}
              onChange={handleTabChange}
              destroyInactiveTabPane={false}
              items={[
                {
                  key: 'details',
                  label: 'Details',
                  children: (
                    <>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="vehicle_subtype" label="Sub Type" extra={subtypeLocked ? 'Subtype is locked while this vehicle has allocations, including saved setups.' : 'Select a subtype first. It sets type, capacity, and surface type, and fills in cost.'} rules={[{ required: true, message: 'Please select a vehicle subtype' }]}>
                            <Select placeholder="Select vehicle subtype" showSearch loading={loadingSubtypes} disabled={loadingSubtypes || subtypeLocked !== false || selectedVehicleForEdit.vehicle_model === 'Generic Model'}
                              onChange={(value) => {
                                const subtype = vehicleSubtypes.find(item => item.vehicle_subtype === value);
                                if (subtype) {
                                  setDisplayCapacity(subtype.vehicle_capacity);
                                  setDisplayType(subtype.vehicle_type);
                                  setDisplaySurfaceType(subtype.surface_type ?? '');
                                  vehicleForm.setFieldsValue({ vehicle_cost: subtype.vehicle_cost ?? 0 });
                                }
                              }}>
                              {vehicleSubtypes.map(subtype => (
                                <Option key={subtype.vehicle_subtype} value={subtype.vehicle_subtype}>{subtype.vehicle_subtype}</Option>
                              ))}
                            </Select>
                          </Form.Item>
                          {subtypeLocked === false && <p style={{ color: '#888', fontSize: '12px', marginTop: '-12px' }}>Choose Other if none of the listed subtypes fits.</p>}
                          <Form.Item
                            name="vehicle_name"
                            label="Name"
                            rules={[
                              { required: true, message: 'Please enter the vehicle name' },
                              { whitespace: true, message: 'Vehicle name cannot be empty' },
                            ]}
                          >
                            <Input
                              placeholder="Enter vehicle name"
                              disabled={selectedVehicleForEdit.vehicle_model === 'Generic Model'}
                            />
                          </Form.Item>
                          {selectedVehicleForEdit.vehicle_model === 'Generic Model' && (
                            <p style={{ color: '#888', fontSize: '12px', marginTop: '-10px' }}>
                              Generic vehicle names cannot be changed.
                            </p>
                          )}
                          <Form.Item label="Type" htmlFor="vehicle-type" extra="Loaded from the subtype; determines which allocation lists include this vehicle.">
                            <Input id="vehicle-type" value={displayType} disabled placeholder="Select a subtype" />
                          </Form.Item>
                          <Form.Item
                            name="vehicle_model"
                            label="Model"
                            rules={[
                              { required: true, message: 'Please enter the vehicle model' },
                              { whitespace: true, message: 'Vehicle model cannot be empty' },
                              { max: 100, message: 'Vehicle model must be 100 characters or fewer' },
                            ]}
                          >
                            <Input
                              placeholder="Enter vehicle model"
                              disabled={selectedVehicleForEdit.vehicle_model === 'Generic Model'}
                            />
                          </Form.Item>
                          {selectedVehicleForEdit.vehicle_model === 'Generic Model' && (
                            <p style={{ color: '#888', fontSize: '12px', marginTop: '-10px' }}>
                              Generic vehicle models cannot be changed.
                            </p>
                          )}
                        </Col>
                        <Col span={12}>
                          <Form.Item name="vehicle_make" label="Make">
                            <Input maxLength={100} placeholder="Vehicle make" />
                          </Form.Item>
                          <Form.Item name="vehicle_year" label="Year">
                            <Input maxLength={10} placeholder="Vehicle year" />
                          </Form.Item>
                          <Form.Item label="Surface Type" htmlFor="vehicle-surface-type" extra="Loaded from the subtype. Existing allocated vehicles keep their saved surface type.">
                            <Input id="vehicle-surface-type" value={displaySurfaceType} disabled placeholder="Select a subtype" />
                          </Form.Item>
                          <Form.Item label="Capacity" htmlFor="vehicle-capacity" extra="Loaded from the selected vehicle subtype. Change capacity in the subtype table.">
                            <InputNumber id="vehicle-capacity" value={displayCapacity} disabled placeholder="Select a subtype" min={0} style={{ width: '100%' }} />
                          </Form.Item>
                          <Form.Item name="vehicle_serial_number" label="Serial Number">
                            <Input placeholder="Serial number" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="vehicle_cost" label="Vehicle Costs ($)">
                            <InputNumber
                              placeholder="0.00"
                              min={0}
                              step={0.01}
                              precision={2}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item name="color" label="Color">
                            <Input placeholder="Vehicle color" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="driver_name" label="Driver Name">
                            <Input placeholder="Assigned driver name" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item 
                            name="driver_email" 
                            label="Driver Email"
                            rules={[
                              { type: 'email', message: 'Please enter a valid email address' }
                            ]}
                          >
                            <Input placeholder="driver@example.com" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="provider"
                            label="Provider"
                            rules={[{ required: true, message: 'Please select a provider' }]}
                          >
                            <Select placeholder="Select provider">
                              <Option value="Viaesys">Viaesys</Option>
                              <Option value="Samsara">Samsara</Option>
                              <Option value="Ignore">Ignore</Option>
                              <Option value="n/a">n/a</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="fuel_type_name" label="Fuel Type">
                            <Input placeholder="Gasoline, Diesel, etc." />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item name="primary_meter_unit" label="Primary Meter Unit">
                            <Select placeholder="Select unit">
                              <Option value="km">km</Option>
                              <Option value="mi">mi</Option>
                              <Option value="hours">hours</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="primary_meter_value" label="Primary Meter Value">
                            <InputNumber placeholder="Current reading" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  ),
                },
                {
                  key: 'registration',
                  label: 'Registration & Ownership',
                  children: (
                    <>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name="license_plate" label="License Plate">
                          <Input placeholder="Plate number" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="registration_state" label="State/Province">
                          <Input placeholder="Registration state" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="registration_expiration_month" label="Expiry Month">
                          <InputNumber placeholder="1-12" min={1} max={12} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item name="ownership" label="Ownership">
                            <Select placeholder="Select ownership" allowClear>
                              <Option value="Leased">Leased</Option>
                              <Option value="Rented">Rented</Option>
                              <Option value="Owned">Owned</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="msrp_cents" label="MSRP (cents)">
                          <InputNumber placeholder="e.g., 5000000 = $50,000" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="estimated_resale_price_cents" label="Est. Resale (cents)">
                          <InputNumber placeholder="e.g., 3000000 = $30,000" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name="transponder_407_etr" label="407 ETR Transponder">
                          <Input placeholder="Transponder ID" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="gps_unit" label="GPS Unit">
                          <Input placeholder="GPS unit ID" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="dashcam_id" label="Dashcam ID">
                          <Input placeholder="Dashcam ID" />
                        </Form.Item>
                      </Col>
                    </Row>
                    </>
                  ),
                },
                {
                  key: 'files',
                  label: 'Documents & Photos',
                  children: renderVehicleFiles(),
                },
              ].filter(tab => !isCreatingVehicle || tab.key !== 'files')}
            />

            <Form.Item style={{ marginTop: '24px' }}>
              <Button type="primary" htmlType="submit" size="large" block loading={isSavingVehicle} disabled={isSavingVehicle || loadingSubtypes || subtypeLocked === null || (!subtypeLocked && !vehicleSubtypes.length)}>
                {isCreatingVehicle ? 'Create Vehicle' : 'Update Vehicle'}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>


      <h3 style={{fontWeight:"bold",}}>
        Vehicle Time Sheet:
      </h3>
      <Collapse>
        <Collapse.Panel header="Generate Viaesys Vehicles Time Sheet" key="1">
          <Space direction="vertical" size="large">
            <div>
              <label style={{paddingRight: 10}}>Start Date:</label>
              <DatePicker
                showTime={{
                  format: "HH:mm",
                  defaultValue: dayjs("00:00", "HH:mm"),
                }}
                format="YYYY-MM-DD HH:mm"
                value={startDate}
                onChange={handleStartDateChange}
                allowClear={false}
              />
            </div>
            <div>
              <label style={{paddingRight: 10}}>End Date:</label>
              <DatePicker
                showTime={{
                  format: "HH:mm",
                  defaultValue: dayjs("00:00", "HH:mm"),
                }}
                format="YYYY-MM-DD HH:mm"
                value={endDate}
                onChange={handleEndDateChange}
                allowClear={false}
              />
            </div>
            <Button type="primary" onClick={generateTimeSheet}>
              Generate Time Sheet CSV
            </Button>
          </Space>
        </Collapse.Panel>
      </Collapse>
    </Box>
  );
};

export default EquipmentListView;
